import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  MessageSquare,
  Sparkles,
  Users,
  Bell,
  Search,
  PlusSquare,
  User,
  LogOut,
  ShieldCheck,
  Settings,
  BadgeCheck,
  Send,
} from "lucide-react";
import { useAuth, useNotificationsSocket } from "../../lib/auth";
import { api, fileUrl } from "../../lib/api";

const nav = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/debates", label: "Debates", icon: MessageSquare },
  { to: "/thoughts", label: "Thoughts", icon: Sparkles },
  { to: "/create", label: "Create", icon: PlusSquare, accent: true },
  { to: "/groups", label: "Groups", icon: Users },
  { to: "/chats", label: "Chats", icon: Send, key: "chats" },
  { to: "/search", label: "Search", icon: Search },
  { to: "/notifications", label: "Alerts", icon: Bell },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadChats, setUnreadChats] = useState(0);

  const loadUnread = () => {
    api.get("/chats/unread_total").then((r) => setUnreadChats(r.data.unread || 0)).catch(() => {});
  };
  useEffect(() => { loadUnread(); }, []);
  useNotificationsSocket((n) => { if (n.kind === "message") loadUnread(); });

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-border bg-background sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="px-6 py-8">
          <NavLink to="/home" className="brand-mark text-4xl block leading-none" data-testid="brand-logo">
            Truebate<span className="text-[hsl(var(--primary))]">.</span>
          </NavLink>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {nav.map(({ to, label, icon: Icon, accent, key }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl transition-all text-[15px] ${
                  isActive
                    ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] font-semibold"
                    : accent
                    ? "text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-soft))] font-medium"
                    : "text-foreground hover:bg-muted"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={2} />
              <span className="flex-1">{label}</span>
              {key === "chats" && unreadChats > 0 && (
                <span
                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="chats-nav-badge"
                >{unreadChats > 99 ? "99+" : unreadChats}</span>
              )}
            </NavLink>
          ))}
          <NavLink
            to={`/profile/${user?.username}`}
            data-testid="nav-profile"
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3 rounded-xl transition-all text-[15px] ${
                isActive
                  ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] font-semibold"
                  : "text-foreground hover:bg-muted"
              }`
            }
          >
            <div className="w-5 h-5 rounded-full bg-[hsl(var(--primary-soft))] flex items-center justify-center overflow-hidden">
              {user?.profile_photo ? (
                <img src={fileUrl(user.profile_photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[9px] font-bold text-[hsl(var(--primary))]">{user?.username?.[0]?.toUpperCase()}</span>
              )}
            </div>
            Profile
          </NavLink>
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl transition-all text-[15px] ${
                  isActive ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] font-semibold" : "text-foreground hover:bg-muted"
                }`
              }
            >
              <ShieldCheck className="w-5 h-5" />
              Admin
            </NavLink>
          )}
        </nav>
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-3 p-2 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary-soft))] border border-border flex items-center justify-center overflow-hidden">
              {user?.profile_photo ? (
                <img src={fileUrl(user.profile_photo)} alt="me" className="w-full h-full object-cover" />
              ) : (
                <span className="font-bold text-[hsl(var(--primary))]">{user?.username?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate flex items-center gap-1">
                @{user?.username}
                {user?.verification_status === "verified" && (
                  <BadgeCheck className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-white" />
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">{user?.full_name}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              className="flex items-center justify-center gap-1 py-2 rounded-xl bg-muted hover:bg-[hsl(var(--primary-soft))] text-xs font-semibold transition-colors"
              onClick={() => navigate("/settings")}
              data-testid="settings-btn"
            >
              <Settings className="w-3.5 h-3.5" />
              Settings
            </button>
            <button
              className="flex items-center justify-center gap-1 py-2 rounded-xl bg-muted hover:bg-[hsl(var(--oppose-soft))] hover:text-[hsl(var(--oppose))] text-xs font-semibold transition-colors"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 glass-nav border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <NavLink to="/home" className="brand-mark text-2xl">
            Truebate<span className="text-[hsl(var(--primary))]">.</span>
          </NavLink>
          <div className="flex items-center gap-1">
            <NavLink to="/search" className="p-2 rounded-full hover:bg-muted" data-testid="mobile-search"><Search className="w-5 h-5" /></NavLink>
            <NavLink to="/chats" className="p-2 rounded-full hover:bg-muted relative" data-testid="mobile-chats">
              <Send className="w-5 h-5" />
              {unreadChats > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-bold flex items-center justify-center">
                  {unreadChats > 9 ? "9+" : unreadChats}
                </span>
              )}
            </NavLink>
            <NavLink to="/notifications" className="p-2 rounded-full hover:bg-muted" data-testid="mobile-alerts"><Bell className="w-5 h-5" /></NavLink>
            <NavLink to={`/profile/${user?.username}`} className="p-2 rounded-full hover:bg-muted" data-testid="mobile-profile"><User className="w-5 h-5" /></NavLink>
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass-nav border-t border-border">
        <div className="grid grid-cols-5">
          <NavLink to="/home" data-testid="mobile-nav-home" className={({isActive}) => `flex flex-col items-center gap-1 py-3 ${isActive ? 'text-[hsl(var(--primary))]' : 'text-foreground'}`}>
            <Home className="w-6 h-6" strokeWidth={isActive => isActive ? 2.5 : 1.75} />
          </NavLink>
          <NavLink to="/debates" data-testid="mobile-nav-debates" className={({isActive}) => `flex flex-col items-center gap-1 py-3 ${isActive ? 'text-[hsl(var(--primary))]' : 'text-foreground'}`}>
            <MessageSquare className="w-6 h-6" />
          </NavLink>
          <NavLink to="/create" data-testid="mobile-nav-create" className="flex flex-col items-center gap-1 py-3">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center">
              <PlusSquare className="w-5 h-5" />
            </div>
          </NavLink>
          <NavLink to="/thoughts" data-testid="mobile-nav-thoughts" className={({isActive}) => `flex flex-col items-center gap-1 py-3 ${isActive ? 'text-[hsl(var(--primary))]' : 'text-foreground'}`}>
            <Sparkles className="w-6 h-6" />
          </NavLink>
          <NavLink to="/groups" data-testid="mobile-nav-groups" className={({isActive}) => `flex flex-col items-center gap-1 py-3 ${isActive ? 'text-[hsl(var(--primary))]' : 'text-foreground'}`}>
            <Users className="w-6 h-6" />
          </NavLink>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0 pb-16 lg:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
